import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Stars } from "@/components/Rating";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, EyeOff, RotateCcw, Flag, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "published", label: "Published" },
  { key: "pending", label: "Pending" },
  { key: "reported", label: "Reported" },
  { key: "hidden", label: "Hidden" },
];
const badge = (s) => ({ published: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", hidden: "bg-rose-100 text-rose-700" }[s] || "bg-zinc-100 text-zinc-700");

export function AdminReviews() {
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {review, action}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/reviews", { params: { filter } }).then((r) => setRows(r.data.reviews)).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const doModerate = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/reviews/${modal.review.id}/${modal.action}`, { reason: reason.trim() });
      toast.success(modal.action === "hide" ? "Review hidden" : "Review restored");
      setModal(null); setReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-reviews">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`reviews-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${filter === f.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 border border-zinc-200"}`}>{f.label}</button>
        ))}
      </div>

      {loading ? <div className="mt-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div> : (
        <div className="mt-4 space-y-3">
          {rows.length === 0 ? <p className="text-sm text-zinc-400">No reviews match this filter.</p> :
            rows.map((r) => (
              <div key={r.id} data-testid={`admin-review-${r.id}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Stars value={r.rating} size={14} />
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badge(r.status)}`}>{r.status}</span>
                  <span className="text-xs text-zinc-400">{r.reviewer_role} → {r.subject_role}</span>
                  {r.reported && <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs text-rose-600"><Flag className="h-3 w-3" /> {r.reports.length} report(s)</span>}
                </div>
                <p className="mt-1 text-sm text-zinc-500">{r.campaign_title}</p>
                {r.comment && <p className="mt-2 rounded-lg bg-zinc-50 p-2 text-sm text-zinc-700">{r.comment}</p>}
                {r.reports?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {r.reports.map((rep, i) => <p key={i} className="text-xs text-rose-600">Reported ({rep.role}): {rep.reason || "no reason given"}</p>)}
                  </div>
                )}
                {r.hidden_reason && <p className="mt-2 text-xs text-rose-500">Hidden reason: {r.hidden_reason}</p>}
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <a href={r.collab_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"><ExternalLink className="h-3 w-3" /> Collaboration</a>
                  {r.status !== "hidden" ? (
                    <Button size="sm" variant="outline" data-testid={`hide-${r.id}`} onClick={() => { setModal({ review: r, action: "hide" }); setReason(""); }} className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50"><EyeOff className="mr-1 h-3.5 w-3.5" /> Hide</Button>
                  ) : (
                    <Button size="sm" variant="outline" data-testid={`restore-${r.id}`} onClick={() => { setModal({ review: r, action: "restore" }); setReason(""); }} className="rounded-full"><RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore</Button>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
      <p className="mt-4 text-xs text-zinc-400">Admins can hide or restore reviews with a recorded reason. Reviews and ratings are never edited.</p>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-md" data-testid="moderate-dialog">
          <DialogHeader>
            <DialogTitle>{modal?.action === "hide" ? "Hide this review" : "Restore this review"}</DialogTitle>
            <DialogDescription>A moderation reason is required and recorded in the audit trail.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="moderate-reason" rows={3} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder="Reason for this moderation action" className="resize-none" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModal(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="moderate-confirm" onClick={doModerate} disabled={busy} className="rounded-full bg-zinc-900 hover:bg-zinc-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
